import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import MainLayout from './layouts/MainLayout';
import BatchLayout from './layouts/BatchLayout';
import DashboardPage from './components/dashboard/DashboardPage';
import BatchList from './components/BatchList';
import BatchDetail from './components/BatchDetail';
import ProfileViewer from './components/ProfileViewer';
import PromptLogViewer from './components/PromptLogViewer';
import SchemaDraftWorkspace from './components/SchemaDraftWorkspace';
import RuleLibraryWorkbench from './components/RuleLibraryWorkbench';
import ProjectManager from './components/ProjectManager';
import SchemaSetManager from './components/SchemaSetManager';
import { ConnectorCatalogPage } from './components/ConnectorCatalog';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import LandingPage from './pages/LandingPage';
import ProfilePage from './pages/ProfilePage';
import RoleGuard from './components/common/RoleGuard';
import FeatureGuard from './components/common/FeatureGuard';
import { useAuth } from './context/AuthContext';
import { featureFlags } from './config';
import { FeatureName } from './types/featureNames';
import AdminDashboard from './pages/admin/AdminDashboard';
import HelpPage from './pages/HelpPage';
import Workspace from './components/workspace/Workspace';
import BatchScheduleTab from './components/schedule/BatchScheduleTab';
import ErrorBoundary from './components/common/ErrorBoundary';

// Protected Route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />}
      />

      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />}
      />

      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Protected Routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Routes>
                <Route path="/dashboard" element={<ErrorBoundary variant="section" sectionName="Dashboard"><DashboardPage /></ErrorBoundary>} />
                <Route path="/batches" element={<ErrorBoundary variant="section" sectionName="Batches"><BatchList /></ErrorBoundary>} />

                {/* Batch Context Routes */}
                <Route path="/batches/:batchId" element={<ErrorBoundary variant="section" sectionName="Batch Workspace"><BatchLayout /></ErrorBoundary>}>
                  <Route index element={<BatchDetail />} />
                  <Route path="overview" element={<BatchDetail />} />
                  {/* Legacy routes render the workspace directly */}
                  <Route path="mapping" element={<Workspace />} />
                  <Route path="agent" element={<Workspace />} />
                  <Route path="profile" element={<ProfileViewer />} />
                  <Route path="schedule" element={<BatchScheduleTab />} />
                  {featureFlags.workspaceExperiment && (
                    <Route path="workspace" element={<Workspace />} />
                  )}
                  {featureFlags.canvasExperiment && (
                    <Route
                      path="prompts"
                      element={
                        <FeatureGuard feature={FeatureName.SHOW_LOGS}>
                          <PromptLogViewer />
                        </FeatureGuard>
                      }
                    />
                  )}
                  {featureFlags.canvasExperiment && (
                    <Route
                      path="logs"
                      element={
                        <FeatureGuard feature={FeatureName.SHOW_LOGS}>
                          <PromptLogViewer />
                        </FeatureGuard>
                      }
                    />
                  )}
                </Route>

                <Route path="/projects/drafts/:draftId" element={<ErrorBoundary variant="section" sectionName="Schema Draft"><SchemaDraftWorkspace /></ErrorBoundary>} />
                <Route path="/projects" element={<ErrorBoundary variant="section" sectionName="Projects"><ProjectManager /></ErrorBoundary>} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/schema-sets" element={<ErrorBoundary variant="section" sectionName="Schema Sets"><SchemaSetManager /></ErrorBoundary>} />
                <Route path="/rules" element={<ErrorBoundary variant="section" sectionName="Rule Library"><RuleLibraryWorkbench /></ErrorBoundary>} />
                <Route path="/connectors" element={<ErrorBoundary variant="section" sectionName="Connectors"><ConnectorCatalogPage /></ErrorBoundary>} />
                <Route path="/help" element={<HelpPage />} />

                {/* Admin Routes */}
                <Route
                  path="/admin/*"
                  element={
                    <RoleGuard roles={['admin']}>
                      <ErrorBoundary variant="section" sectionName="Admin">
                        <AdminDashboard />
                      </ErrorBoundary>
                    </RoleGuard>
                  }
                />

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </MainLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

export default App;
